/**
 * NOTE (Yuxin, 2026-02-02): SemanticModifiers Component
 *
 * This component provides the unified UI for LLM-based visual editing when a semantic
 * element is selected.
 *
 * Features:
 * - Shows element type badge (Text, Button, Container, etc.)
 * - V2 modifier components (PaddingModifier, MarginModifier, StyleModifier, etc.)
 * - On Click section for viewing/editing click handlers
 */

import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback } from "react";

// NOTE (Derek, 2026-02-19): Agent-ui modifier components that read from the
// semantic element selection instead of v2's optimistic modifier values.
import AppearanceModifier from "@agent-ui/features/modifiers/base/AppearanceModifier";
import AspectRatioModifier from "@agent-ui/features/modifiers/base/AspectRatioModifier";
import { AssetModifier } from "@agent-ui/features/modifiers/base/AssetModifier";
import BackgroundModifierComponent from "@agent-ui/features/modifiers/base/BackgroundModifier";
import BorderModifier from "@agent-ui/features/modifiers/base/BorderModifier";
import ContainerSizeModifier from "@agent-ui/features/modifiers/base/ContainerSizeModifier";
import ContentModifierComponent from "@agent-ui/features/modifiers/base/ContentModifier";
import GapModifierComponent from "@agent-ui/features/modifiers/base/GapModifier";
import ImageDimensionsModifier from "@agent-ui/features/modifiers/base/ImageDimensionsModifier";
import MarginModifierComponent from "@agent-ui/features/modifiers/base/MarginModifier";
import ObjectFitModifier from "@agent-ui/features/modifiers/base/ObjectFitModifier";
import PaddingModifierComponent from "@agent-ui/features/modifiers/base/PaddingModifier";
import ShadowModifierComponent from "@agent-ui/features/modifiers/base/ShadowModifier";
import StyleModifier from "@agent-ui/features/modifiers/base/StyleModifier";
import TextAlignmentModifierComponent from "@agent-ui/features/modifiers/base/TextAlignmentModifier";
import TextColorModifierComponent from "@agent-ui/features/modifiers/base/TextColorModifier";
import { useMorphModifier } from "@agent-ui/features/modifiers/hooks/useMorphModifier";
import {
  useHasSemanticSelection,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChatFileDropZoneProvider } from "@v2/features/chat/hooks/useChatFileDropZone";
import { Loader2, Trash2, X } from "lucide-react";

// =============================================================================
// Element Type Display Names
// =============================================================================

const ELEMENT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  text: "Text",
  button: "Button",
  container: "Container",
  link: "Link",
  image: "Image",
  video: "Video",
  icon: "Icon",
  input: "Input",
  "product-section": "Product Section",
};

// =============================================================================
// Helper Components
// =============================================================================

type ModifierRowProps = React.PropsWithChildren<{
  title: string;
}>;

const ModifierRow: React.FC<ModifierRowProps> = ({ title, children }) => {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3 items-start">
      <span className="text-xs font-medium text-default h-[24px] flex flex-col justify-center">
        {title}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
};

interface ModifierPanelHeaderProps {
  onDelete: () => void;
  onClose: () => void;
}

const ModifierPanelHeader: React.FC<ModifierPanelHeaderProps> = ({
  onDelete,
  onClose,
}) => (
  <div className="flex items-center justify-between flex-shrink-0 mb-3">
    <span className="text-sm font-medium text-default">Edit</span>
    <div className="flex items-center gap-1">
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={onDelete}
        aria-label="Delete element"
      >
        <Trash2 />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={onClose}
        aria-label="Close"
      >
        <X />
      </Button>
    </div>
  </div>
);

// =============================================================================
// Semantic Text Modifier Block
// Uses the same v2 modifier components as TextModifierBlock but wires to Groq
// =============================================================================

interface SemanticTextModifierBlockProps {
  applyDirectStyle: (modifiers: UniversalModifierValue[]) => void;
}

const SemanticTextModifierBlock: React.FC<SemanticTextModifierBlockProps> = ({
  applyDirectStyle,
}) => {
  return (
    <div className="flex flex-col divide-y-0.5 divide-border">
      {/* Content & Alignment Section */}
      <div className="flex flex-col gap-3 pb-4">
        <ModifierRow title="Content">
          <ContentModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Alignment">
          <TextAlignmentModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>

      {/* Style Section */}
      <div className="flex flex-col gap-3 py-4">
        <ModifierRow title="Style">
          <StyleModifier onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Color">
          <TextColorModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>

      {/* Spacing Section */}
      <div className="flex flex-col gap-3 pt-4">
        <ModifierRow title="Padding">
          <PaddingModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>
    </div>
  );
};

// =============================================================================
// Semantic Container Modifier Block
// Uses the same v2 modifier components as ContainerModifierBlock but wires to Groq
// =============================================================================

interface SemanticContainerModifierBlockProps {
  applyDirectStyle: (modifiers: UniversalModifierValue[]) => void;
}

const SemanticContainerModifierBlock: React.FC<
  SemanticContainerModifierBlockProps
> = ({ applyDirectStyle }) => {
  return (
    <div className="flex flex-col divide-y-0.5 divide-border">
      {/* Size Section */}
      <div className="flex flex-col gap-3 pb-4">
        <ModifierRow title="Size">
          <ContainerSizeModifier
            onWidthChange={applyDirectStyle}
            onHeightChange={applyDirectStyle}
            heightExcludeOptions={["full"]}
          />
        </ModifierRow>
      </div>

      {/* Gap Section */}
      <div className="flex flex-col gap-3 py-4">
        <ModifierRow title="Gap">
          <GapModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>

      {/* Spacing & Background Section */}
      <div className="flex flex-col gap-3 py-4">
        <ModifierRow title="Padding">
          <PaddingModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Margin">
          <MarginModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Background">
          <BackgroundModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>

      {/* Border Section */}
      <div className="flex flex-col gap-3 py-4">
        <ModifierRow title="Border">
          <BorderModifier onChange={applyDirectStyle} />
        </ModifierRow>
      </div>

      {/* Appearance Section */}
      <div className="flex flex-col gap-3 pt-4">
        <ModifierRow title="Appearance">
          <AppearanceModifier onChange={applyDirectStyle} />
        </ModifierRow>
      </div>
    </div>
  );
};

// =============================================================================
// Semantic Image Modifier Block
// Uses AssetModifier, ImageDimensionsModifier, ObjectFitModifier, etc. wired to Groq
// =============================================================================

interface SemanticImageModifierBlockProps {
  applyDirectStyle: (modifiers: UniversalModifierValue[]) => void;
}

const SemanticImageModifierBlock: React.FC<SemanticImageModifierBlockProps> = ({
  applyDirectStyle,
}) => {
  return (
    <div className="flex flex-col divide-y-0.5 divide-border">
      {/* Media Section */}
      <div className="flex flex-col gap-3 pb-4">
        <ModifierRow title="Media">
          <ChatFileDropZoneProvider>
            <AssetModifier
              onAssetChange={(assetSource) => {
                applyDirectStyle([{ type: "asset", value: assetSource ?? "" }]);
              }}
            />
          </ChatFileDropZoneProvider>
        </ModifierRow>
      </div>

      {/* Size Section */}
      <div className="flex flex-col gap-3 py-4">
        <ModifierRow title="Size">
          <ImageDimensionsModifier
            onWidthChange={applyDirectStyle}
            onHeightChange={applyDirectStyle}
          />
        </ModifierRow>
        <ModifierRow title="Aspect Ratio">
          <AspectRatioModifier
            onChange={(modifier) => applyDirectStyle([modifier])}
          />
        </ModifierRow>
        <ModifierRow title="Object Fit">
          <ObjectFitModifier
            onChange={(modifier) => applyDirectStyle([modifier])}
          />
        </ModifierRow>
      </div>

      {/* Border & Appearance Section */}
      <div className="flex flex-col gap-3 pt-4">
        <ModifierRow title="Border">
          <BorderModifier onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Appearance">
          <AppearanceModifier onChange={applyDirectStyle} />
        </ModifierRow>
        <ModifierRow title="Shadow">
          <ShadowModifierComponent onChange={applyDirectStyle} />
        </ModifierRow>
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export default function SemanticModifiers() {
  const semanticElement = useSemanticElement();
  const hasSelection = useHasSemanticSelection();
  const {
    applyDirectStyle,
    isProcessing,
    error,
    clearSelection,
    clearError,
    deleteElement,
  } = useMorphModifier();

  // Get display info from the selected element
  const element = semanticElement;
  const elementType = element?.elementType ?? "container";
  const elementName = element?.name ?? "Selected Element";
  const displayName = ELEMENT_TYPE_DISPLAY_NAMES[elementType] ?? "Element";

  // Handle close button
  const handleClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // Handle delete button
  const handleDelete = useCallback(() => {
    void deleteElement();
  }, [deleteElement]);

  // Determine which modifier block to show based on element type
  const renderModifierBlock = () => {
    switch (elementType) {
      case "text":
        return (
          <SemanticTextModifierBlock applyDirectStyle={applyDirectStyle} />
        );
      case "image":
        return (
          <SemanticImageModifierBlock applyDirectStyle={applyDirectStyle} />
        );
      case "container":
      case "button":
      case "link":
        return (
          <SemanticContainerModifierBlock applyDirectStyle={applyDirectStyle} />
        );
      default:
        // For other element types, show container modifiers as a reasonable default
        return (
          <SemanticContainerModifierBlock applyDirectStyle={applyDirectStyle} />
        );
    }
  };

  if (!hasSelection || !element) {
    return null;
  }

  // Edit mode: render the modifier controls
  return (
    <div
      className={twMerge(
        "relative flex max-h-full h-full overflow-hidden w-full p-4 pb-0 flex-col flex-start border-border rounded-xl bg-white border-0.5 shadow-sm",
        isProcessing && "pointer-events-none",
      )}
    >
      {/* Loading overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-white/60 rounded-xl z-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      )}

      <ModifierPanelHeader onDelete={handleDelete} onClose={handleClose} />

      {/* Element type badge + name */}
      <div className="flex items-center gap-2 min-w-0 mb-4">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-md bg-default/10 text-xs font-medium text-default flex-shrink-0"
          title={`Element type: ${displayName}`}
        >
          {displayName}
        </span>
        <span className="text-xs text-muted truncate" title={elementName}>
          {elementName}
        </span>
      </div>

      <div className="border-t-0.5 border-border -mx-4 flex-shrink-0" />

      {/* Content */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 py-4 overflow-y-auto overflow-x-hidden no-scrollbar">
        {/* Error display with dismiss */}
        {error && (
          <Alert
            variant="error"
            size="inline"
            description={error}
            onClose={clearError}
          />
        )}

        {/* V2 Modifier Block - same UI as v2, wired to Groq */}
        {renderModifierBlock()}
      </div>
    </div>
  );
}
